/**
 * Service Access Hook
 * 
 * Provides centralized access control based on subscription context from backend.
 * This hook reads the dynamic 'services' map from the user's subscription data
 * and provides convenient methods to check access permissions.
 * 
 * The services map is built dynamically from backend's SERVICE_ACCESS_CONFIG,
 * making it automatically extensible when new services are added.
 */
import { useAuth } from './useAuth';

/**
 * useAccess hook
 * Access service permissions from any component
 * 
 * @returns {Object} Access control utilities and state
 */
export function useAccess() {
  const { user, isAuthenticated } = useAuth();
  const subscription = user?.subscription;
  const services = subscription?.services || {};

  return {
    // =========================================================================
    // User Info
    // =========================================================================
    
    /** Current user type: 'subscriber', 'free_tier', 'authenticated', 'anonymous' */
    userType: subscription?.user_type || 'anonymous',
    
    /** Whether user is authenticated */
    isAuthenticated,
    
    /** Whether user has an active paid subscription */
    isSubscriber: subscription?.user_type === 'subscriber',
    
    /** Whether user is on free tier */
    isFreeTier: subscription?.user_type === 'free_tier',

    // =========================================================================
    // Subscription Details
    // =========================================================================
    
    /** Active subscription info object (null if no active subscription) */
    subscriptionInfo: subscription?.subscription || null,
    
    /** Whether subscription is currently active */
    isSubscriptionActive: subscription?.subscription?.is_active || false,
    
    /** Current plan name (e.g., 'Gold', 'Silver', 'Advance+') */
    planName: subscription?.subscription?.plan_name,
    
    /** Current plan category ('stock_recommendations' or 'live_corporate_announcements') */
    planCategory: subscription?.subscription?.plan_category,
    
    /** Days remaining in current subscription */
    daysRemaining: subscription?.subscription?.days_remaining || 0,
    
    /** Subscription expiry date (ISO string) */
    expiryDate: subscription?.subscription?.expiry_date,

    // =========================================================================
    // Free Tier Details
    // =========================================================================
    
    /** Free tier info object (null if not on free tier) */
    freeTierInfo: subscription?.free_tier || null,
    
    /** Whether free tier is currently active */
    isFreeTierActive: subscription?.free_tier?.is_active || false,

    // =========================================================================
    // Services Map (Dynamic from Backend)
    // =========================================================================
    
    /** Full services map from backend */
    services,

    // =========================================================================
    // Access Check Methods
    // =========================================================================
    
    /**
     * Check if user can access a specific service
     * @param {string} serviceName - Service name (e.g., 'performance_report', 'live_ica')
     * @returns {boolean} Whether user has access
     */
    canAccess: (serviceName) => {
      return services[serviceName]?.has_access || false;
    },

    /**
     * Get full service info object
     * @param {string} serviceName - Service name
     * @returns {Object} Service info with has_access, display_name, description, is_purchasable, can_customize, etc.
     */
    getService: (serviceName) => {
      return services[serviceName] || { 
        has_access: false, 
        display_name: serviceName,
        description: '',
        access_type: 'unknown',
        is_purchasable: false,
        can_customize: false
      };
    },

    /**
     * Get service display name
     * @param {string} serviceName - Service name
     * @returns {string} Human-readable service name
     */
    getServiceName: (serviceName) => {
      return services[serviceName]?.display_name || serviceName;
    },

    /**
     * Check if a service is directly purchasable
     * @param {string} serviceName - Service name
     * @returns {boolean} Whether the service can be directly purchased
     */
    isPurchasable: (serviceName) => {
      return services[serviceName]?.is_purchasable || false;
    },

    /**
     * Get the category/service that needs to be purchased to access a locked service
     * @param {string} serviceName - Service name
     * @returns {string|null} The category to purchase or included_in info
     */
    getPurchaseInfo: (serviceName) => {
      const service = services[serviceName];
      if (!service) return null;
      
      if (service.is_purchasable) {
        return { type: 'direct', category: service.purchasable_category };
      }
      if (service.included_in_category) {
        return { type: 'category', category: service.included_in_category };
      }
      if (service.included_in_service) {
        return { type: 'service', service: service.included_in_service, minTier: service.min_tier };
      }
      return null;
    },

    /**
     * Check if user can customize a tiered service (e.g., select custom stocks/indexes)
     * @param {string} serviceName - Service name (typically 'live_ica')
     * @returns {boolean} Whether user can customize
     */
    canCustomize: (serviceName) => {
      return services[serviceName]?.can_customize || false;
    },

    /**
     * Check tier-level access for tiered services
     * @param {string} serviceName - Service name (typically 'live_ica')
     * @param {string} requiredTier - Required tier ('basic', 'advance', 'advance_plus')
     * @returns {boolean} Whether user's tier is sufficient
     */
    hasTierAccess: (serviceName, requiredTier) => {
      const service = services[serviceName];
      if (!service?.has_access) return false;
      
      const tierOrder = ['basic', 'advance', 'advance_plus'];
      const userTierLevel = tierOrder.indexOf(service.tier || '');
      const requiredLevel = tierOrder.indexOf(requiredTier);
      return userTierLevel >= requiredLevel;
    },

    // =========================================================================
    // Live ICA Convenience Accessors
    // =========================================================================
    
    /** Live ICA specific access info */
    liveIca: {
      hasAccess: services.live_ica?.has_access || false,
      tier: services.live_ica?.tier || null,
      accessType: services.live_ica?.access_type_detail || null,
      maxIndexes: services.live_ica?.max_indexes || 0,
      maxCustomStocks: services.live_ica?.max_custom_stocks || 0,
      defaultIndex: services.live_ica?.default_index || null,
      canCustomize: services.live_ica?.can_customize || false,
      isPurchasable: services.live_ica?.is_purchasable || false,
      tierDetails: services.live_ica?.tier_details || {},
    },

    // =========================================================================
    // Computed Service Lists
    // =========================================================================
    
    /** List of services user can access */
    accessibleServices: Object.entries(services)
      .filter(([_, s]) => s.has_access)
      .map(([name, s]) => ({ name, ...s })),

    /** List of services user cannot access (for upgrade prompts) */
    lockedServices: Object.entries(services)
      .filter(([_, s]) => !s.has_access)
      .map(([name, s]) => ({ name, ...s })),

    /** List of directly purchasable services */
    purchasableServices: Object.entries(services)
      .filter(([_, s]) => s.is_purchasable)
      .map(([name, s]) => ({ name, ...s })),
  };
}

export default useAccess;
